/**
 * useAlertsFeed — the alerts half of the D-2 overlay manager.
 *
 * Every endpoint, param, dedupe Set, poll cadence, subscription and claim/
 * escalate flow here came verbatim from the old CrmAlertHost, moved behind a
 * hook so the stack can own the corner. The kind table lives on in `alertKinds`
 * rather than being copied, so the eight cards cannot drift apart.
 *
 * Three deliberate differences, and only three:
 *  - No `slice(0, MAX_ALERTS)`. A fourth hot lead queues behind the visible
 *    three instead of being silently discarded.
 *  - Escalate-at-T-0 sweeps EVERY alert assigned to me, not just the ones on
 *    screen — a queued card's 3-minute window is just as real.
 *  - One chime recipe for all eight kinds, fired once per refresh batch rather
 *    than once per card (three simultaneous AudioContexts is a defect, not a
 *    feature). OS notifications keep their per-kind title, body and tag exactly.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { subscribeLeadAlerts, subscribeAgentEvents } from '../../../lib/realtimeTransport';
import { useAuth } from '../../../contexts/AuthContext';
import { KINDS, defFor, isAgentKind, playChime, fireOsNotification } from './alertKinds';
import type { AlertKind, CrmAlert } from './alertKinds';

const POLL_MS = 30_000;

/** The one pair, for every kind. v1 varied it per kind; the stack chimes once. */
const CHIME_NOTES: [number, number] = [880, 1174.7];

export interface AlertsFeed {
  alerts: CrmAlert[];
  /** Hide locally and keep it hidden across refreshes (v1 `dismissLocal`). */
  dismiss: (id: string) => void;
  /** Claim, then navigate — aborting the navigation if someone else won it. */
  open: (a: CrmAlert) => Promise<void>;
}

export function useAlertsFeed(nowMs: number): AlertsFeed {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<CrmAlert[]>([]);

  // Alerts already chimed/notified (so a re-fetch never re-alerts), alerts the
  // user dismissed locally (kept hidden across re-fetches), and alerts whose
  // escalate POST we've already fired.
  const shownRef = useRef<Set<string>>(new Set());
  const dismissedRef = useRef<Set<string>>(new Set());
  const escalatedPostedRef = useRef<Set<string>>(new Set());

  const userRef = useRef(user);
  userRef.current = user;

  const fetchActive = useCallback(async () => {
    try {
      // Two authed feeds share the stack: the classic alerts, and the texting
      // agent's popup-worthy OPEN escalations (§6.7). The agent fetch fails
      // soft — a console without the agent feature still pops hot leads.
      const [alertsRes, agentRes] = await Promise.all([
        api.get<{ alerts: CrmAlert[] }>('/api/crm/alerts/active'),
        api.get<{ escalations: any[] }>('/api/crm/agent/escalations', { params: { status: 'open', popup: 'true', limit: 10 } })
          .catch(() => ({ data: { escalations: [] } })),
      ]);
      const agentCards: CrmAlert[] = (agentRes.data?.escalations || []).map((e: any) => ({
        id: e.id,
        kind: `agent_${e.kind}` as AlertKind,
        contact_id: e.contact_id,
        first_name: e.contact_name || null,
        study_name: null,
        status: e.assigned_user_id ? 'assigned' : 'escalated',
        assigned_user_id: e.assigned_user_id || null,
        assigned_user_name: null,
        created_at: e.created_at,
        // Most agent escalations carry no client deadline (the step-7 worker
        // tick owns re-routing); escalate_at = created_at keeps the shape
        // without implying one. The §6.7b pair DOES have one: payload.release_at
        // is the instant the agent stops waiting and answers the lead itself.
        escalate_at: e.payload?.release_at || e.created_at,
      }));
      const list = [...(alertsRes.data?.alerts || []), ...agentCards.filter((a) => KINDS[a.kind])]
        .filter((a) => !dismissedRef.current.has(a.id));
      // Notify per card (the OS collapses by tag), but chime once for the batch.
      let arrived = 0;
      for (const a of list) {
        if (shownRef.current.has(a.id)) continue;
        shownRef.current.add(a.id);
        arrived += 1;
        fireOsNotification(a);
      }
      if (arrived > 0) playChime(CHIME_NOTES);
      setAlerts(list);
    } catch {
      /* a missed fetch is harmless — the next ping/poll reconciles */
    }
  }, []);

  // Subscribe once to the fixed alert topics; re-fetch on every ping. Plus a
  // slow poll as a safety net for a missed broadcast and to drop expired cards.
  useEffect(() => {
    const unsubscribe = subscribeLeadAlerts(() => { fetchActive(); });
    const unsubscribeAgent = subscribeAgentEvents(() => { fetchActive(); });
    fetchActive();
    const id = window.setInterval(fetchActive, POLL_MS);
    return () => { unsubscribe(); unsubscribeAgent(); window.clearInterval(id); };
  }, [fetchActive]);

  // When MY assigned window elapses, ask the server to escalate to everyone (the
  // worker sweep is the backstop if this tab is gone). Guarded to fire once.
  // Applies to both classic kinds; agent escalations have no client-side
  // deadline (the step-7 worker tick owns their re-routing), so they skip it.
  // Runs over the WHOLE feed — a card waiting behind the overflow still owes
  // its escalation.
  useEffect(() => {
    const uid = userRef.current?.id;
    for (const a of alerts) {
      if (isAgentKind(a.kind)) continue;
      if (a.status !== 'assigned' || a.assigned_user_id !== uid) continue;
      if (escalatedPostedRef.current.has(a.id)) continue;
      if (new Date(a.escalate_at).getTime() <= nowMs) {
        escalatedPostedRef.current.add(a.id);
        api.post(`/api/crm/alerts/${a.kind}/${a.id}/escalate`).catch(() => {});
      }
    }
  }, [alerts, nowMs]);

  const dismiss = useCallback((id: string) => {
    dismissedRef.current.add(id);
    setAlerts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const open = useCallback(async (a: CrmAlert) => {
    dismiss(a.id);
    try {
      if (isAgentKind(a.kind)) {
        // Agent escalations claim through their own CAS endpoint; a lost race
        // (409) still opens the contact — reading never hurts.
        await api.post(`/api/crm/agent/escalations/${a.id}/claim`);
      } else {
        const res = await api.post<{ ok: boolean; alreadyClaimed: boolean; claimedBy: string | null }>(
          `/api/crm/alerts/${a.kind}/${a.id}/claim`,
        );
        const data = res.data;
        // Someone beat us to it — don't hijack this recruiter into the record.
        if (data?.alreadyClaimed && data.claimedBy && data.claimedBy !== userRef.current?.id) return;
      }
    } catch {
      /* claim failed — still open the contact so they can call */
    }
    navigate(defFor(a).href(a));
  }, [dismiss, navigate]);

  return { alerts, dismiss, open };
}

export default useAlertsFeed;
